/**
 * Shop Admin API Client
 * Handles authenticated requests to shop-api worker
 */

import { getAPIBaseURL, getLoginURL } from '@weretradeit/env-helpers';

// Environment-aware API endpoints
const API_BASE = import.meta.env.VITE_SHOP_API_URL || getAPIBaseURL('shop').replace('/api/shop', '');
const AUTH_BASE = import.meta.env.VITE_AUTH_API_URL || getAPIBaseURL('auth');

export interface User {
  id: string;
  username: string;
  displayName?: string;
  email?: string;
  role?: string;
  avatar?: string;
}

export interface Order {
  id: string;
  email: string;
  name: string;
  address: string;
  items: OrderItem[];
  subtotal: number;
  shipping: number;
  total: number;
  status: 'pending' | 'paid' | 'shipped' | 'delivered' | 'cancelled';
  paymentId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface OrderItem {
  id: string;
  title: string;
  price: number;
  quantity: number;
  image?: string;
}

export interface Category {
  id: string;
  title: string;
  copy?: string;
  icon?: string;
  href?: string;
  cta?: string;
  sortOrder: number;
  active: boolean;
}

export interface Product {
  id: string;
  title: string;
  price: number;
  vendor: string;
  category?: string;
  heroImage?: string;
  tags?: string[];
  featured: boolean;
  published: boolean;
}

export interface NewsletterSubscriber {
  email: string;
  status: 'active' | 'unsubscribed';
  subscribedAt: string;
  unsubscribedAt?: string;
}

export interface DLQEntry {
  id: string;
  platform: string;
  sync_type: string;
  timestamp: string;
  error: string;
  retry_count: number;
  next_retry_at: string | null;
}

export interface MarketplaceOrder {
  id: number;
  invoice_number: string;
  invoice_date: string;
  platform: string;
  status: string;
  buyer: string | null;
  items_count: number;
  total_amount: number | null;
  shipping_amount: number | null;
  currency: string;
  courier: string | null;
  tracking: string | null;
}

export interface Conversation {
  id: number;
  platform: string;
  platform_thread_id: string;
  buyer_name: string | null;
  buyer_id: string | null;
  subject: string | null;
  status: 'open' | 'resolved' | 'ignored';
  created_at: string;
  updated_at: string;
}

export interface Message {
  id: number;
  conversation_id: number;
  platform_message_id: string | null;
  direction: 'inbound' | 'outbound';
  body: string;
  created_at: string;
  read: number; // 0 or 1
  attachments?: {
    id: number;
    file_name: string;
    content_type: string;
    r2_key: string;
  }[];
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

async function apiFetch<T>(path: string, options: RequestInit & { base?: string } = {}): Promise<T> {
  const base = options.base ?? API_BASE;
  const url = `${base}${path}`;
  const res = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }

  return res.json();
}

// Auth
export async function fetchMe(): Promise<User | null> {
  const res = await fetch(`${AUTH_BASE}/me`, { credentials: 'include' });

  // Only a real "not signed in" answer may bounce to the login page.
  if (res.status === 401) {
    window.location.href = getLoginURL();
    return null;
  }

  if (!res.ok) {
    throw new Error(`Auth check failed: HTTP ${res.status}`);
  }

  // A 200 alone is not proof of a session: when /api/auth/* is not routed to
  // the auth worker on this origin, Pages answers with the SPA shell instead.
  // Parsing that as JSON used to throw into a catch-all that redirected to
  // login, and the loop hid the misconfiguration. Check the media type the way
  // the shop-api backend does (backend/src/lib/auth.ts) and fail loudly.
  const contentType = res.headers.get('content-type') || '';
  if (!contentType.toLowerCase().includes('application/json')) {
    throw new Error(
      `Auth check returned "${contentType || 'no content-type'}" instead of JSON — ` +
      `${AUTH_BASE}/me is not routed to the auth worker on this origin`
    );
  }

  return res.json();
}

// Inventar Owl API Base (for DLQ)
const INVENTAR_API_BASE = import.meta.env.VITE_INVENTAR_API_URL || getAPIBaseURL('inventar').replace('/api/inventar', '');

// DLQ
export async function fetchDLQ(): Promise<DLQEntry[]> {
  return apiFetch('/api/dlq', { base: INVENTAR_API_BASE });
}

export async function retryDLQEntry(id: string): Promise<{ ok: boolean; message: string }> {
  return apiFetch(`/api/dlq/${id}/retry`, {
    method: 'POST',
    base: INVENTAR_API_BASE
  });
}

export async function deleteDLQEntry(id: string): Promise<void> {
  await apiFetch(`/api/dlq/${id}`, {
    method: 'DELETE',
    base: INVENTAR_API_BASE
  });
}

export async function retryAllDLQ(): Promise<{ ok: boolean; processed: number; success: number; failed: number }> {
  return apiFetch('/api/dlq/retry-all', {
    method: 'POST',
    base: INVENTAR_API_BASE
  });
}

export async function fetchMarketplaceOrders(status?: string): Promise<MarketplaceOrder[]> {
  const query = status ? `?status=${status}` : '';
  return apiFetch(`/api/invoices${query}`, { base: INVENTAR_API_BASE });
}

export async function fetchConversations(limit: number = 50, offset: number = 0): Promise<{ data: Conversation[], hasMore: boolean }> {
  return apiFetch(`/crm/conversations?limit=${limit}&offset=${offset}`, { base: INVENTAR_API_BASE });
}

export async function fetchMessages(conversationId: number, limit: number = 50, offset: number = 0): Promise<{ data: Message[], hasMore: boolean }> {
  return apiFetch(`/crm/conversations/${conversationId}/messages?limit=${limit}&offset=${offset}`, { base: INVENTAR_API_BASE });
}

export async function sendReply(conversationId: number, body: string): Promise<{ ok: boolean; message_id: number; note: string }> {
  return apiFetch(`/crm/messages/reply`, {
    method: 'POST',
    base: INVENTAR_API_BASE,
    body: JSON.stringify({ conversation_id: conversationId, body }),
  });
}

export async function syncConversations(): Promise<{ ok: boolean; synced: number; note: string }> {
  return apiFetch(`/crm/sync`, {
    method: 'POST',
    base: INVENTAR_API_BASE,
  });
}

// Orders
export async function fetchOrders(params?: {
  status?: string;
  limit?: number;
  offset?: number;
  search?: string;
}): Promise<{ orders: Order[]; total: number }> {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', String(params.limit));
  if (params?.offset) query.set('offset', String(params.offset));
  if (params?.search) query.set('search', params.search);
  const qs = query.toString();
  return apiFetch(`/shop/orders${qs ? `?${qs}` : ''}`);
}

export async function fetchOrder(id: string): Promise<Order> {
  return apiFetch(`/shop/order/${id}`);
}

export async function updateOrderStatus(id: string, status: Order['status']): Promise<Order> {
  return apiFetch(`/shop/order/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

// Categories
export async function fetchCategories(): Promise<Category[]> {
  return apiFetch('/shop/categories');
}

export async function createCategory(data: Omit<Category, 'id'>): Promise<Category> {
  return apiFetch('/shop/categories', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCategory(id: string, data: Partial<Category>): Promise<Category> {
  return apiFetch(`/shop/categories/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteCategory(id: string): Promise<void> {
  await apiFetch(`/shop/categories/${id}`, { method: 'DELETE' });
}

// Products
export async function fetchProducts(params?: {
  category?: string;
  vendor?: string;
  published?: boolean;
  featured?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<{ products: Product[]; total: number }> {
  const query = new URLSearchParams();
  if (params?.category) query.set('category', params.category);
  if (params?.vendor) query.set('vendor', params.vendor);
  if (params?.published !== undefined) query.set('published', String(params.published));
  if (params?.featured !== undefined) query.set('featured', String(params.featured));
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', String(params.limit));
  if (params?.offset) query.set('offset', String(params.offset));
  const qs = query.toString();
  return apiFetch(`/shop/products${qs ? `?${qs}` : ''}`);
}

export async function updateProduct(id: string, data: Partial<Product>): Promise<Product> {
  return apiFetch(`/shop/products/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

// Newsletter
export async function fetchNewsletterSubscribers(params?: {
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<{ subscribers: NewsletterSubscriber[]; total: number }> {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', String(params.limit));
  if (params?.offset) query.set('offset', String(params.offset));
  const qs = query.toString();
  return apiFetch(`/shop/newsletter/list${qs ? `?${qs}` : ''}`);
}

export async function deleteNewsletterSubscriber(email: string): Promise<void> {
  await apiFetch(`/shop/newsletter/${encodeURIComponent(email)}`, { method: 'DELETE' });
}

// Analytics
export interface AnalyticsSummary {
  revenue: { total: number; thisMonth: number; lastMonth: number };
  orders: { total: number; pending: number; paid: number; shipped: number; delivered: number };
  averageOrderValue: number;
  topProducts: Array<{ id: string; title: string; revenue: number; units: number }>;
}

export async function fetchAnalyticsSummary(): Promise<AnalyticsSummary> {
  return apiFetch('/shop/analytics');
}

// Email Templates
export interface EmailTemplate {
  id: string;
  name: string;
  subject_template: string;
  html_template: string;
  text_template: string | null;
  sender_email: string | null;
  sender_name: string | null;
  description: string | null;
  variables_schema: string | null;
  updated_at: string;
}

export async function fetchEmailTemplates(): Promise<{ templates: EmailTemplate[] }> {
  return apiFetch('/shop/admin/email-templates');
}

export async function updateEmailTemplate(id: string, data: Partial<EmailTemplate>): Promise<void> {
  await apiFetch(`/shop/admin/email-templates/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function sendTestEmail(templateId: string, email: string): Promise<void> {
  await apiFetch('/shop/admin/email-templates/test', {
    method: 'POST',
    body: JSON.stringify({ templateId, email }),
  });
}

export async function fetchShopSettings(): Promise<{ settings: Record<string, unknown> }> {
  return apiFetch('/shop/settings');
}

// Legal compliance (GET /api/shop/legal/audit, admin only)
export type LegalStatus = 'pass' | 'warn' | 'fail' | 'manual';
export type LegalCheckKind = 'settings' | 'page' | 'template' | 'static' | 'manual';

export interface LegalAuditCheck {
  kind: LegalCheckKind;
  status: LegalStatus;
  detail: string;
}

export interface LegalAuditItem {
  id: string;
  title: string;
  legalBasis: string;
  pagePath: string | null;
  status: LegalStatus;
  checks: LegalAuditCheck[];
}

export interface LegalAudit {
  ok: boolean;
  generatedAt: string;
  storefrontOrigin: string;
  reviewedAt: string | null;
  summary: Record<LegalStatus, number>;
  items: LegalAuditItem[];
}

export async function fetchLegalAudit(): Promise<LegalAudit> {
  return apiFetch('/shop/legal/audit');
}

// Withdrawal function (LC-20): declarations submitted on the storefront /widerruf
// (GET /api/shop/withdrawals, PATCH /api/shop/withdrawals/:id — admin only)
export type WithdrawalStatus = 'received' | 'processed' | 'rejected';

export interface Withdrawal {
  id: string;
  order_id: string | null;
  order_reference: string;
  order_date: string | null;
  customer_name: string;
  customer_email: string;
  customer_address: string | null;
  message: string | null;
  status: WithdrawalStatus;
  received_at: string;
  acknowledged_at: string | null;
  processed_at: string | null;
  admin_note: string | null;
}

export interface WithdrawalList {
  ok: boolean;
  withdrawals: Withdrawal[];
  total: number;
  limit: number;
  offset: number;
}

export async function fetchWithdrawals(
  params: { status?: WithdrawalStatus; limit?: number; offset?: number } = {},
): Promise<WithdrawalList> {
  const query = new URLSearchParams();
  if (params.status) query.set('status', params.status);
  if (params.limit !== undefined) query.set('limit', String(params.limit));
  if (params.offset !== undefined) query.set('offset', String(params.offset));
  const qs = query.toString();
  return apiFetch(`/shop/withdrawals${qs ? `?${qs}` : ''}`);
}

export async function updateWithdrawal(
  id: string,
  patch: { status?: WithdrawalStatus; admin_note?: string | null },
): Promise<{ ok: boolean; withdrawal: Withdrawal }> {
  return apiFetch(`/shop/withdrawals/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

/**
 * PATCH /api/shop/settings without throwing on 4xx: the caller needs the
 * error body (`error`, `key`, `message`, `keys`) to tell the operator which
 * field the server rejected.
 */
export async function patchShopSettings(
  changes: Record<string, unknown>,
): Promise<{ ok: boolean; status: number; body: Record<string, unknown> }> {
  const res = await fetch(`${API_BASE}/shop/settings`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(changes),
  });
  const body = await res.json().catch(() => ({})) as Record<string, unknown>;
  return { ok: res.ok && body.ok !== false, status: res.status, body };
}
